/** The kinds of scoped views. Everything that differs per kind is keyed on this, so a new kind is one entry. */
export type ScopedViewKind = "tasks" | "reports";

/**
 * A scope narrows a view to one item, the way a route, task list, object or user filter does.
 * The values match the API (`ViewScopeTypeEnum`).
 */
export type ViewScopeType = "propertyRoute" | "taskList" | "property" | "user" | "reportList" | "sort";

export const VIEW_SCOPE_TYPES: readonly ViewScopeType[] = ["propertyRoute", "taskList", "property", "user", "reportList", "sort"];

export const VIEW_SCOPE_TYPES_BY_KIND: Record<ScopedViewKind, readonly ViewScopeType[]> = {
  tasks: ["propertyRoute", "taskList", "property", "user"],
  reports: ["reportList", "sort", "property"],
};

/** The user preference that holds the default view of each kind. */
export const DEFAULT_VIEW_PREFERENCE_KEYS = {
  tasks: "defaultTaskView",
  reports: "defaultReportView",
} as const satisfies Record<ScopedViewKind, string>;

export type DefaultViewPreferenceKey = typeof DEFAULT_VIEW_PREFERENCE_KEYS[ScopedViewKind];

/**
 * The scope a default view opens with, as the effective views return it (`defaultScope`). `user`
 * names a user inside a task list (department) scope: that user's tasks within the department.
 */
export interface DefaultViewScope {
  type: ViewScopeType
  id: number
  user?: number | null
}

/** Error codes of the API on a default view preference, also used for the same checks on the client. */
export type DefaultViewProblemCode
  = | "DEFAULT_VIEW_INVALID_FORMAT"
    | "DEFAULT_VIEW_SCOPE_TYPE_NOT_ALLOWED"
    | "DEFAULT_VIEW_SCOPE_NOT_AT_LOCATION"
    | "DEFAULT_VIEW_PER_PROPERTY_WITH_SCOPE"
    | "DEFAULT_VIEW_SCOPE_NEEDS_LOCATION"
    | "DEFAULT_VIEW_USER_NOT_IN_TASK_LIST"
    | "DEFAULT_VIEW_SCOPE_NOT_READABLE";

export const DEFAULT_VIEW_PROBLEM_CODES: readonly DefaultViewProblemCode[] = [
  "DEFAULT_VIEW_INVALID_FORMAT",
  "DEFAULT_VIEW_SCOPE_TYPE_NOT_ALLOWED",
  "DEFAULT_VIEW_SCOPE_NOT_AT_LOCATION",
  "DEFAULT_VIEW_PER_PROPERTY_WITH_SCOPE",
  "DEFAULT_VIEW_SCOPE_NEEDS_LOCATION",
  "DEFAULT_VIEW_USER_NOT_IN_TASK_LIST",
  "DEFAULT_VIEW_SCOPE_NOT_READABLE",
];

// Same shape as the API: every id has at most 18 digits there, so it always fits in a PHP int. The
// second pair `,user:<id>` only follows a task list scope: a user inside that department.
const PATTERN = /^(?<key>[a-z0-9_]{1,64})(?:@(?<scopeType>[a-zA-Z]+):(?<scopeId>[1-9]\d{0,17})(?:,user:(?<userId>[1-9]\d{0,17}))?)?$/;
const KEY_PATTERN = /^[a-z0-9_]{1,64}$/;

function isViewScopeType(value: string): value is ViewScopeType {
  return (VIEW_SCOPE_TYPES as readonly string[]).includes(value);
}

/**
 * The value of a default view preference: a view key, optionally with one scope
 * (`today`, `walk_route@propertyRoute:5`), where a task list scope may name a user inside it
 * (`task_list@taskList:3,user:41`). Key and scope are one value, so a level that sets a default
 * replaces both and nothing is inherited separately. Mirrors `App\ValueObject\View\DefaultViewReference`.
 */
export class DefaultViewReference {
  private constructor(
    public readonly viewKey: string,
    public readonly scope: DefaultViewScope | null,
  ) {}

  static create(viewKey: string, scope: DefaultViewScope | null = null): DefaultViewReference {
    if (!KEY_PATTERN.test(viewKey)) {
      throw new Error(`"${viewKey}" is not a valid view key.`);
    }
    if (scope === null) {
      return new DefaultViewReference(viewKey, null);
    }
    if (!isViewScopeType(scope.type)) {
      throw new Error(`"${scope.type}" is not a view scope type.`);
    }
    if (!Number.isSafeInteger(scope.id) || scope.id < 1) {
      throw new Error("A default view scope id must be a positive integer.");
    }
    const user = scope.user ?? null;
    if (user === null) {
      return new DefaultViewReference(viewKey, { type: scope.type, id: scope.id });
    }
    if (scope.type !== "taskList") {
      throw new Error("Only a task list scope can name a user in it.");
    }
    if (!Number.isSafeInteger(user) || user < 1) {
      throw new Error("A default view user id must be a positive integer.");
    }
    return new DefaultViewReference(viewKey, { type: scope.type, id: scope.id, user });
  }

  /**
   * Only the shape: a scope type that does not exist still matches, so callers can tell a
   * malformed value from an unknown scope type.
   */
  static matchesFormat(value: string): boolean {
    return PATTERN.test(value);
  }

  static tryParse(value: string): DefaultViewReference | null {
    const groups = PATTERN.exec(value)?.groups;
    if (!groups) {
      return null;
    }
    const { key, scopeType, scopeId, userId } = groups;
    if (scopeType === undefined || scopeId === undefined) {
      return new DefaultViewReference(key, null);
    }
    if (!isViewScopeType(scopeType)) {
      return null;
    }
    const id = Number(scopeId);
    // An id beyond 2^53 cannot be one of our records and would lose precision as a number.
    if (!Number.isSafeInteger(id)) {
      return null;
    }
    if (userId === undefined) {
      return new DefaultViewReference(key, { type: scopeType, id });
    }
    const user = Number(userId);
    if (scopeType !== "taskList" || !Number.isSafeInteger(user)) {
      return null;
    }
    return new DefaultViewReference(key, { type: scopeType, id, user });
  }

  static parse(value: string): DefaultViewReference {
    const reference = DefaultViewReference.tryParse(value);
    if (!reference) {
      throw new Error(`"${value}" is not a valid default view.`);
    }
    return reference;
  }

  /**
   * The problem the API would report for this value on a preference of `kind`, apart from the
   * checks that need the records (location, membership, read rights, the level of the row).
   */
  static problemFor(value: string, kind: ScopedViewKind): DefaultViewProblemCode | null {
    const scopeType = PATTERN.exec(value)?.groups?.scopeType;
    if (!DefaultViewReference.matchesFormat(value)) {
      return "DEFAULT_VIEW_INVALID_FORMAT";
    }
    if (scopeType !== undefined && (!isViewScopeType(scopeType) || !VIEW_SCOPE_TYPES_BY_KIND[kind].includes(scopeType))) {
      return "DEFAULT_VIEW_SCOPE_TYPE_NOT_ALLOWED";
    }
    // A user after a scope other than a task list, or an id beyond the safe integer range.
    return DefaultViewReference.tryParse(value) ? null : "DEFAULT_VIEW_INVALID_FORMAT";
  }

  hasScope(): boolean {
    return this.scope !== null;
  }

  /** The user inside a task list scope, or null. */
  get user(): number | null {
    return this.scope?.user ?? null;
  }

  /** The same reference without the user in its task list: the whole department. */
  withoutUser(): DefaultViewReference {
    if (this.scope === null || this.user === null) {
      return this;
    }
    return DefaultViewReference.create(this.viewKey, { type: this.scope.type, id: this.scope.id });
  }

  isAllowedFor(kind: ScopedViewKind): boolean {
    return this.scope === null || VIEW_SCOPE_TYPES_BY_KIND[kind].includes(this.scope.type);
  }

  equals(other: DefaultViewReference | null): boolean {
    if (!other || other.viewKey !== this.viewKey) {
      return false;
    }
    if (this.scope === null || other.scope === null) {
      return this.scope === other.scope;
    }
    return this.scope.type === other.scope.type
      && this.scope.id === other.scope.id
      && (this.scope.user ?? null) === (other.scope.user ?? null);
  }

  withScope(scope: DefaultViewScope | null): DefaultViewReference {
    return DefaultViewReference.create(this.viewKey, scope);
  }

  format(): string {
    if (this.scope === null) {
      return this.viewKey;
    }
    const base = `${this.viewKey}@${this.scope.type}:${this.scope.id}`;
    return this.user === null ? base : `${base},user:${this.user}`;
  }

  toString(): string {
    return this.format();
  }
}
